import { getConnectedAs } from 'shared/components/testAction/TestActionFunctions';
import { createAxiosInstance } from './apiUtils';
import { queryClient } from './queryClient';
import { queryKeys } from './queryKeys';

export type ReferenceOption = {
  value: string;
  label?: string | null;
  description?: string | null;
  group?: string | null;
};
export type ReferenceDataResponse = {
  options: ReferenceOption[];
  cache_ttl_seconds?: number;
  truncated?: boolean;
};

// `actionType` is the semantic role ("auth" / "pull" / "push" / …) —
// stable across integration types, unlike `actionValue` which is a slug.
export type DraftActionConfig = {
  actionValue: string;
  actionType?: string;
  data: Record<string, unknown>;
};
export type DraftReferenceState = {
  typeId: string;
  owner: string;
  baseUrl?: string;
  configurations: DraftActionConfig[];
  // The auth action's value + schema, if the type has one. buildDraftReference
  // populates both so the widget (which has no provider-actions access via
  // formContext) and the hook agree on which config row IS the auth row
  // (integrations whose auth action isn't literally named "auth") and can
  // gate on the schema-aware AJV check rather than a slug heuristic.
  authValue?: string;
  authSchema?: any;
};

// Tests. Drops settled answers only, like the Map it replaced: removing a query
// that's still fetching would cancel it and reject its caller's promise.
export function clearReferenceOptionsCache() {
  queryClient.removeQueries({
    queryKey: queryKeys.referenceOptions.all,
    predicate: (q) => q.state.fetchStatus !== 'fetching',
  });
}

// Order-insensitive JSON stringify — recursively sorts object keys so
// equivalent drafts produce identical cache keys regardless of how the
// caller assembled them.
export function stableStringify(value: unknown): string {
  // Distinct markers for undefined vs null so cache keys don't collide
  // across {token: undefined} and {token: null}. Emit an unambiguous string
  // for each. Dates go through toISOString so distinct time ranges don't
  // collapse to "{}" (Object.entries on a Date returns []).
  if (value === undefined) return '"__undefined__"';
  if (value === null) return 'null';
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value))
    return '[' + value.map(stableStringify).join(',') + ']';
  const entries = Object.entries(value as Record<string, unknown>).sort(
    ([a], [b]) => (a < b ? -1 : a > b ? 1 : 0),
  );
  return (
    '{' +
    entries
      .map(([k, v]) => JSON.stringify(k) + ':' + stableStringify(v))
      .join(',') +
    '}'
  );
}

// FNV-1a 32-bit hash. Not cryptographic — the goal is to keep credential
// strings from sitting in a heap-retained Map key indefinitely (Copilot's
// concern in the review), not to defeat a targeted attacker.
export function hashString(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

// Kept in the shared cache for as long as the server says the answer is good
// (`cache_ttl_seconds`, 5 minutes if it doesn't say). Concurrent requests for the
// same key share one fetch, and a failure isn't kept, so the next open retries.
async function runWithCache(
  key: string,
  fetcher: () => Promise<ReferenceDataResponse>,
): Promise<ReferenceDataResponse> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.referenceOptions.entry(key),
    queryFn: fetcher,
    staleTime: (query) =>
      ((query.state.data as ReferenceDataResponse | undefined)
        ?.cache_ttl_seconds ?? 300) * 1000,
    gcTime: REFERENCE_GC_MS,
  });
}

// How long an answer nobody is reading stays in memory.
const REFERENCE_GC_MS = 30 * 60 * 1000;

export async function fetchReferenceOptions(
  token: string,
  integrationId: string,
  actionValue: string,
  params: Record<string, unknown>,
): Promise<ReferenceDataResponse> {
  const key = JSON.stringify([integrationId, actionValue, params]);
  return runWithCache(key, async () => {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.post(
      `/v2/integrations/${encodeURIComponent(integrationId)}/actions/${encodeURIComponent(actionValue)}/execute/`,
      { config_overrides: params },
      { timeout: 15000 }, // Cloud Run cold starts: generous, behind a spinner
    );
    return resp.data as ReferenceDataResponse;
  });
}

/** Verify unsaved credentials via the ephemeral execute endpoint. Cache-free
 *  by design — each click is an intentional retry against possibly-edited creds. */
export type TestConnectionResult =
  | { status: 'valid'; connectedAs?: string }
  | { status: 'invalid'; detail?: string }
  | { status: 'error'; detail?: string };

export async function testDraftConnection(
  token: string,
  actionValue: string,
  draft: DraftReferenceState,
): Promise<TestConnectionResult> {
  try {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.post(
      `/v2/integrations/types/${encodeURIComponent(draft.typeId)}/actions/${encodeURIComponent(actionValue)}/execute/`,
      {
        owner: draft.owner,
        base_url: draft.baseUrl ?? '',
        configurations: draft.configurations.map((c) => ({
          action_value: c.actionValue,
          data: c.data,
        })),
        config_overrides: {},
      },
      { timeout: 15000 },
    );
    if (resp.data && resp.data.valid_credentials === false) {
      return { status: 'invalid', detail: resp.data.error ?? resp.data.detail };
    }
    const connectedAs = getConnectedAs(resp.data);
    return connectedAs ? { status: 'valid', connectedAs } : { status: 'valid' };
  } catch (err: any) {
    const httpStatus = err?.response?.status;
    const body = err?.response?.data;
    // detail can arrive as a string (older cdip / FastAPI HTTPException) or
    // a dict {action_id, error} (runner ephemeral responses + newer router
    // 422s that were made shape-consistent). Coerce so the toast template
    // never renders "[object Object]".
    const rawDetail = body?.detail;
    const detail =
      typeof rawDetail === 'string'
        ? rawDetail
        : rawDetail && typeof rawDetail === 'object' && 'error' in rawDetail
          ? String(rawDetail.error)
          : (typeof body === 'string' ? body : undefined) || err?.message;
    // Only 401/403 (direct from cdip or via 502's upstream_status) counts as
    // "invalid credentials" — 404/400/429/5xx aren't cred problems.
    const isAuthReject = (s: unknown) => s === 401 || s === 403;
    if (isAuthReject(httpStatus)) return { status: 'invalid', detail };
    if (httpStatus === 502 && isAuthReject(body?.upstream_status))
      return { status: 'invalid', detail };
    return { status: 'error', detail };
  }
}

export async function fetchDraftReferenceOptions(
  token: string,
  actionValue: string,
  params: Record<string, unknown>,
  draft: DraftReferenceState,
): Promise<ReferenceDataResponse> {
  const key =
    'draft:' +
    draft.typeId +
    ':' +
    actionValue +
    ':' +
    hashString(stableStringify({ params, draft }));
  return runWithCache(key, async () => {
    const axiosInstance = await createAxiosInstance(token);
    const resp = await axiosInstance.post(
      `/v2/integrations/types/${encodeURIComponent(draft.typeId)}/actions/${encodeURIComponent(actionValue)}/execute/`,
      {
        owner: draft.owner,
        base_url: draft.baseUrl ?? '',
        configurations: draft.configurations.map((c) => ({
          action_value: c.actionValue,
          data: c.data,
        })),
        config_overrides: params,
      },
      { timeout: 15000 },
    );
    return resp.data as ReferenceDataResponse;
  });
}
